import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductIframe } from '@/components/ProductDomain/ProductIframe';

interface AdminModuleFrameProps {
  moduleId: string;
  src: string;
  title: string;
}

const loadTimeoutMs = 30000;

export function AdminModuleFrame({
  moduleId,
  src,
  title,
}: AdminModuleFrameProps) {
  const { t } = useTranslation('entity-portal');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const loadTimeoutRef = useRef<number | null>(null);

  const clearLoadTimeout = useCallback(() => {
    if (loadTimeoutRef.current !== null) {
      window.clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);

  const handleLoad = useCallback(() => {
    clearLoadTimeout();
    setIsLoading(false);
    setHasError(false);
  }, [clearLoadTimeout]);

  const handleRetry = useCallback(() => {
    clearLoadTimeout();
    setHasError(false);
    setIsLoading(true);
    setRetryCount((current) => current + 1);
  }, [clearLoadTimeout]);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    clearLoadTimeout();

    loadTimeoutRef.current = window.setTimeout(() => {
      setIsLoading(false);
      setHasError(true);
    }, loadTimeoutMs);

    return clearLoadTimeout;
  }, [clearLoadTimeout, moduleId, retryCount, src]);

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <ProductIframe
        key={`${moduleId}-${retryCount}`}
        src={src}
        title={title}
        onLoad={handleLoad}
      />

      {isLoading ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            bgcolor: 'background.default',
          }}
        >
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            {t('admin.adminModuleLoading', { module: title })}
          </Typography>
        </Box>
      ) : null}

      {hasError ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.92),
          }}
        >
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                {t('common.retry')}
              </Button>
            }
            sx={{ maxWidth: 480, width: '100%' }}
          >
            {t('admin.adminModuleLoadError', { module: title })}
          </Alert>
        </Box>
      ) : null}
    </Box>
  );
}
